import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Hook que mide dinámicamente el alto disponible desde la posición top del ref
 * hasta el borde inferior del viewport. Útil para contenedores scrollables que
 * deben ocupar todo el alto restante.
 *
 * @param {Object} options
 * @param {number} [options.bottomMargin=24] - margen inferior a respetar (px)
 * @param {number} [options.minHeight=320] - alto mínimo (px)
 * @returns {[React.RefObject, string]} ref para asignar al elemento y la altura calculada en px
 */
export const useViewportFillHeight = ({ bottomMargin = 24, minHeight = 320 } = {}) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(`${minHeight}px`);

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const available = window.innerHeight - top - bottomMargin;
    setHeight(`${Math.max(minHeight, available)}px`);
  }, [bottomMargin, minHeight]);

  useLayoutEffect(() => {
    recompute();
  });

  useEffect(() => {
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [recompute]);

  return [ref, height];
};
